import React from 'react';
import {
  Badge,
  TextField,
  CardContent,
  Checkbox,
  Grid,
  InputLabel,
  makeStyles,
  Button,
  Theme,
  Select,
  MenuItem,
  OutlinedInput
} from '@material-ui/core';
import {
  LuiVerticalFilter,
  LuiFilterController,
  CustomDateFnsUtils,
  LuiToggleButtonGroup
} from '@jda/lui-common-component-library';
import Avatar from '@material-ui/core/Avatar';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import { Sliders, ArrowMinimize } from '@jda/lui-common-icon-library';
import { MuiPickersUtilsProvider, DateTimePicker } from '@material-ui/pickers';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import moment from 'moment-timezone';
import { AutoCompleteComponent } from './AutoComplete';

interface CheckboxListDataType {
  key: string;
  displayName: string;
  count?: number | string;
  fillColor?: string;
}

export interface FilterControllerDataProps {
  onChange: (data?: any) => void;
  onBlur?: (data: any) => void;
  value?: any;
  name?: string;
}

export interface FilterValuesType {
  [name: string]: any;
}

export interface FilterDatOnChange {
  previousValue: any;
  currentValue: any;
  filtersData: FilterValuesType;
}

interface BadeColor {
  color?: string;
}

interface SliderProps {
  children: React.ReactElement;
  open: boolean;
  value: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  checkboxListRoot: {
    padding: theme.spacing(0),
    backgroundColor: 'transparent',
    '& .MuiListItem-root': {
      padding: 0,
      paddingLeft: theme.spacing(0.5),
      '& .MuiListItemIcon-root': {
        minWidth: 'unset'
      }
    }
  },
  container: {
    width: '100%'
  },
  cardHeader: {
    borderColor: '#EBEBEB',
    borderBottom: '1px ridge',
    paddingRight: '16px',
    paddingLeft: '0px'
  },
  avatar: {
    backgroundColor: '#FFF',
    minWidth: '100px'
  },
  slider: {
    color: '#3F4756',
    paddingRight: '4px',
    paddingBottom: '8px'
  },
  minimize: {
    color: '#3F4756',
    paddingRight: '4px',
    paddingBottom: '8px',
    marginLeft: '20px',
    cursor: 'pointer'
  },
  main: {
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: '23vw'
  },
  chip: {
    marginTop: theme.spacing(1)
  },
  label: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '18px',
    color: '#3F4756',
    paddingBottom: '10px'
  },
  resetButton: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    alignItems: 'center'
  },
  inputField: {
    left: theme.spacing(0),
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '16px',
    lineHeight: theme.spacing(19),
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary
  },
  filterContent: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  buttonMargin: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    alignItems: 'center',
    marginLeft: theme.spacing(2)
  }
}));

const badgeStyles = makeStyles(() => ({
  listBadgeColor: (props: BadeColor) => ({
    '& .MuiBadge-badge': {
      backgroundColor: props.color
    }
  })
}));

export const VerticalFilter = (props: any) => {
  const classes = useStyles();

  const getItemKeys = (filterControllerProps: FilterControllerDataProps) => {
    if (filterControllerProps?.value && Array.isArray(filterControllerProps.value)) {
      return filterControllerProps.value.map((item: CheckboxListDataType) => item.key);
    }
    return [];
  };

  const handleToggle = (value: CheckboxListDataType, filterControllerProps: FilterControllerDataProps) => () => {
    const currentIndex = getItemKeys(filterControllerProps).indexOf(value.key);
    const newChecked = [...filterControllerProps.value];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    return filterControllerProps.onChange(newChecked);
  };

  const isItemChecked = (filterControllerProps: FilterControllerDataProps, value: CheckboxListDataType) => {
    const keys = getItemKeys(filterControllerProps);
    return keys.indexOf(value.key) !== -1;
  };

  const getBadgeColor = (value: CheckboxListDataType) => {
    const badgeClass = badgeStyles({ color: value.fillColor });
    return badgeClass.listBadgeColor;
  };

  const renderCheckboxList = (filterControllerProps: FilterControllerDataProps, listData: CheckboxListDataType[]) => {
    return (
      <List className={classes.checkboxListRoot}>
        {listData.map(value => {
          const labelId = `checkbox-list-label-${value.key}`;
          return (
            <ListItem
              data-id="filter-list-item"
              onClick={handleToggle(value, filterControllerProps)}
              key={value.key}
              role={undefined}
              button
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  color="primary"
                  checked={isItemChecked(filterControllerProps, value)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value.displayName} />
              {value.count && (
                <ListItemSecondaryAction>
                  <Badge badgeContent={value.count} max={999} className={getBadgeColor(value)} />
                </ListItemSecondaryAction>
              )}
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderToggle = (filterControllerProps: FilterControllerDataProps, listData: any) => {
    return (
      <LuiToggleButtonGroup
        onButtonChange={(item, index) => filterControllerProps.onChange({ value: item, index })}
        // className={classes.buttonTabStyles}
        data-id="filter-toggle"
        buttonGroupData={listData}
        size="small"
        activeButtonIndex={filterControllerProps.value.index}
      />
    );
  };

  const renderTextField = (filterControllerProps: FilterControllerDataProps) => {
    return (
      <TextField
        inputProps={{ 'aria-label': 'standard No Field' }}
        fullWidth
        placeholder={`Enter ${filterControllerProps.name}`}
        data-id="filter-text-comp"
        value={filterControllerProps.value}
        onChange={(e: any) => {
          filterControllerProps.onChange(e.target.value);
        }}
      />
    );
  };

  const numberRangeField = (filterControllerProps: FilterControllerDataProps) => {
    return (
      <Grid container spacing={3}>
        <Grid item sm={6}>
          <TextField
            inputProps={{ 'aria-label': 'standard No Field' }}
            fullWidth
            placeholder="From"
            data-id="filter-text-comp"
            value={filterControllerProps.value.fromErrorCode || ''}
            onChange={(e: any) => {
              if (e.target.value !== '') {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  fromErrorCode: e.target.value
                });
              } else {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  fromErrorCode: null
                });
              }
            }}
          />
        </Grid>
        <Grid item sm={6}>
          <TextField
            inputProps={{ 'aria-label': 'standard No Field' }}
            fullWidth
            placeholder="To"
            data-id="filter-text-comp"
            value={filterControllerProps.value.toErrorCode || ''}
            onChange={(e: any) => {
              if (e.target.value !== '') {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  toErrorCode: e.target.value
                });
              } else {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  toErrorCode: null
                });
              }
            }}
          />
        </Grid>
      </Grid>
    );
  };

  // const renderSlider = (filterControllerProps: FilterControllerDataProps) => {
  //   return (
  //     <Slider
  //       onChange={(_, value) => {
  //         filterControllerProps.onChange(value);
  //       }}
  //       ValueLabelComponent={ValueLabelComponent}
  //       valueLabelDisplay="auto"
  //       value={filterControllerProps.value}
  //       max={10000}
  //       step={1000}
  //       getAriaLabel={(index: number) => 'revenue impact' + index}
  //     />
  //   );
  // };

  const renderDateRange = (filterControllerProps: FilterControllerDataProps) => {
    return (
      <MuiPickersUtilsProvider utils={CustomDateFnsUtils}>
        <Grid container spacing={3}>
          <Grid item sm={6}>
            <DateTimePicker
              format="MM/dd/yyyy H:mm:ss"
              value={filterControllerProps.value.begin}
              variant="dialog"
              label={'From'}
              key="fromDate"
              data-id="filter-from-date"
              onChange={(date: any) => {
                if (date != null) {
                  filterControllerProps.onChange({
                    ...filterControllerProps.value,
                    begin: moment(date).format('YYYY-MM-DD H:mm')
                  });
                } else {
                  filterControllerProps.onChange({
                    ...filterControllerProps.value,
                    begin: null
                  });
                }
              }}
              animateYearScrolling={false}
              inputProps={{ 'aria-label': 'date picker' }}
              className={classes.inputField}
              maxDate={new Date()}
            />
          </Grid>
          <Grid item sm={6}>
            <DateTimePicker
              format="MM/dd/yyyy H:mm:ss"
              value={filterControllerProps.value.end}
              variant="dialog"
              label={'To'}
              key="toDate"
              data-id="filter-to-date"
              clearable
              onChange={(date: any) => {
                if (date != null) {
                  filterControllerProps.onChange({
                    ...filterControllerProps.value,
                    end: moment(date).format('YYYY-MM-DD H:mm')
                  });
                } else {
                  filterControllerProps.onChange({
                    ...filterControllerProps.value,
                    end: null
                  });
                }
              }}
              animateYearScrolling={false}
              inputProps={{ 'aria-label': 'date picker' }}
              className={classes.inputField}
              maxDate={new Date()}
            />
          </Grid>
        </Grid>
      </MuiPickersUtilsProvider>
    );
  };

  const renderSearch = (filterControllerProps: FilterControllerDataProps, comp: any) => {
    return (
      <AutoCompleteComponent
        values={comp.options || []}
        placeholder={comp.placeholder}
        value={[...filterControllerProps.value]}
        onChange={(value: any) => {
          filterControllerProps.onChange(value);
        }}
        name={filterControllerProps.name}
      />
    );
  };

  const renderCount = (filterControllerProps: FilterControllerDataProps, comp: any) => {
    return (
      <Grid container>
        <Grid item xs={3}>
          <Select
            value={filterControllerProps?.value?.symbol}
            onChange={e => {
              if (e.target.value !== '') {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  symbol: e.target.value
                });
              }
            }}
          >
            <MenuItem value="<">{'<'}</MenuItem>
            <MenuItem value=">">{'>'}</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={9}>
          <TextField
            inputProps={{ 'aria-label': 'standard No Field' }}
            fullWidth
            placeholder={`Enter ${filterControllerProps.name}`}
            data-id="filter-text-comp"
            value={filterControllerProps?.value?.value || ''}
            onChange={(e: any) => {
              filterControllerProps.onChange({
                ...filterControllerProps.value,
                value: e.target.value
              });
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderServiceSelect = (filterControllerProps: FilterControllerDataProps, comp: any) => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Select
            fullWidth
            value={
              filterControllerProps?.value?.service
                ? filterControllerProps?.value?.service
                : props?.filterCriteria.service || []
            }
            renderValue={(selected: any) => selected.join(', ')}
            input={<OutlinedInput label="Tag" />}
            onChange={e => {
              if (e.target.value !== '') {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  service: e.target.value
                });
              }
            }}
            multiple
          >
            {props?.workflowList?.map((item: any) => (
              <MenuItem value={item?.workflowCode} key={item?.workflowCode}>
                <Checkbox
                  checked={
                    (filterControllerProps?.value?.service
                      ? filterControllerProps?.value?.service
                      : props.filterCriteria.service
                    )?.indexOf(item?.workflowCode) > -1
                  }
                />
                <ListItemText primary={item?.workflowName} />
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    );
  };

  const renderSelect = (filterControllerProps: FilterControllerDataProps, comp: any) => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Select
            fullWidth
            value={filterControllerProps?.value?.service || []}
            onChange={e => {
              if (e.target.value !== '') {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  service: e.target.value
                });
              }
            }}
          >
            {props?.workflowList?.map((item: any) => (
              <MenuItem value={item?.workflowCode}>{item?.workflowName}</MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    );
  };
  const renderMultiSelect = (filterControllerProps: FilterControllerDataProps, comp: any) => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Select
            fullWidth
            value={
              filterControllerProps?.value?.participantType
                ? filterControllerProps?.value?.participantType
                : props?.filterCriteria.participantType || []
            }
            renderValue={(selected: any) => selected.join(', ')}
            input={<OutlinedInput label="Tag" />}
            onChange={e => {
              if (e.target.value !== '') {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  participantType: e.target.value
                });
              }
            }}
            multiple
          >
            {props?.participantTypeData?.map((item: any) => (
              <MenuItem value={item?.value} key={item?.value}>
                <Checkbox
                  checked={
                    (filterControllerProps?.value?.participantType
                      ? filterControllerProps?.value?.participantType
                      : props?.filterCriteria.participantType
                    )?.indexOf(item?.value) > -1
                  }
                />
                <ListItemText primary={item?.value} />
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    );
  };

  const renderSelectRetry = (filterControllerProps: FilterControllerDataProps, comp: any) => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Select
            fullWidth
            value={
              filterControllerProps?.value?.retryStatus
                ? filterControllerProps.value.retryStatus
                : props?.filterCriteria.retryStatus || []
            }
            renderValue={(selected: any) => selected.join(', ')}
            input={<OutlinedInput label="Tag" />}
            onChange={e => {
              if (e.target.value !== '') {
                filterControllerProps.onChange({
                  ...filterControllerProps.value,
                  retryStatus: e.target.value
                });
              }
            }}
            multiple
          >
            {props?.retryStatusData?.map((item: any) => (
              <MenuItem value={item?.value} key={item?.value}>
                <Checkbox
                  checked={
                    (filterControllerProps?.value?.retryStatus
                      ? filterControllerProps?.value?.retryStatus
                      : props?.filterCriteria.retryStatus
                    )?.indexOf(item?.value) > -1
                  }
                />
                <ListItemText primary={item?.value} />
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    );
  };

  // const renderDate = (filterControllerProps: FilterControllerDataProps) => {
  //   return (
  //     <MuiPickersUtilsProvider utils={CustomDateFnsUtils}>
  //       <KeyboardDatePicker
  //         format="MM/dd/yyyy"
  //         // disableToolbar
  //         variant="inline"
  //         label=""
  //         value={filterControllerProps.value}
  //         onChange={(date: any) => {
  //           filterControllerProps.onChange(date);
  //         }}
  //         animateYearScrolling={false}
  //         inputProps={{ 'aria-label': 'simple date picker' }}
  //       />
  //     </MuiPickersUtilsProvider>
  //   );
  // };

  // const valuetext = (value: number) => {
  //   return `${value / 1000}K`;
  // };

  // const ValueLabelComponent = (props: SliderProps) => {
  //   const { children, open, value } = props;
  //   return (
  //     <Tooltip open={open} enterTouchDelay={0} placement="top" title={valuetext(value)} arrow>
  //       {children}
  //     </Tooltip>
  //   );
  // };

  const onFilterComponentChange = (data: FilterDatOnChange) => {
    console.debug('filter previous value', data.previousValue);
    console.debug('filter current value', data.currentValue);
    console.debug('filter values', data.filtersData);
  };

  const dataOnFiltersReset = (event: any, filterValues: FilterValuesType) => {
    if (props.workflowCode !== 'freightTracking' && props.workflowCode !== undefined) {
      props.filterCriteria.retryCount.value = null;
      props.filterCriteria.timeTaken.value = null;
      props.filterCriteria.responseHttpStatus.fromErrorCode = null;
      props.filterCriteria.responseHttpStatus.toErrorCode = null;
      props.filterCriteria.initialResponseHttpStatus.fromErrorCode = null;
      props.filterCriteria.initialResponseHttpStatus.toErrorCode = null;
    }
    props.resetFilter();
  };

  const dataOnFiltersApply = (event: any, filterValues: FilterValuesType) => {
    props.applyFilter({ ...filterValues });
  };

  const renderSubFilterComp = (props: any, comp: any) => {
    const fieldType = comp.type;
    switch (fieldType) {
      case 'checkbox':
        return renderCheckboxList(props, comp.data);
      case 'toggle':
        return renderToggle(props, comp.data);
      // case 'slider':
      //   return renderSlider(props);
      case 'daterange':
        return renderDateRange(props);
      // case 'date':
      //   return renderDate(props);
      // case 'text':
      //   return renderTextField(props);
      case 'numberRange':
        return numberRangeField(props);
      case 'search':
        return renderSearch(props, comp);
      case 'numberLimit':
        return renderCount(props, comp);
      case 'serviceSelect':
        return renderServiceSelect(props, comp);
      case 'multiSelect':
        return renderMultiSelect(props, comp);
      case 'select':
        return renderSelect(props, comp);
      case 'selectRetry':
        return renderSelectRetry(props, comp);
      default:
        return renderTextField(props);
    }
  };

  return (
    <Card id="card-header">
      <CardHeader
        className={classes.cardHeader}
        avatar={
          <div className={classes.main}>
            <Avatar aria-label="Sliders" className={classes.avatar}>
              <Sliders color="disabled" fontSize="small" className={classes.slider} />
              <InputLabel className={classes.label}>Filters</InputLabel>
            </Avatar>
            <ArrowMinimize
              color="disabled"
              fontSize="default"
              className={classes.minimize}
              onClick={props.closeFilter}
            />
            {props.showAddNotification && (
              <Button
                data-id="Notification-Button"
                className={classes.buttonMargin}
                variant="contained"
                color="primary"
                disableElevation
                onClick={props.handleCreateNotificationDialog}
              >
                Add Notification
              </Button>
            )}
          </div>
        }
      />

      <CardContent style={{ padding: '0 0 0 0' }}>
        <LuiVerticalFilter
          data-id="Lui-vertical-filter"
          hideResetButton={false}
          hideApplyButton={false}
          onFiltersChange={onFilterComponentChange}
          onFiltersReset={dataOnFiltersReset}
          onFiltersSubmit={dataOnFiltersApply}
        >
          {props.filterConfig.map((item: any, indx: number) => (
            <LuiFilterController
              key={`${item.id}+${props.filterCriteria[item.id]}${indx}`}
              filterName={item.id}
              isDefaultExpanded={
                item.isDefaultExpanded ||
                props.filterCriteria[item.id]?.length > 0 ||
                props.filterCriteria[item.id]?.value ||
                props.filterCriteria[item.id]?.fromErrorCode
              }
              filterLabel={item.label}
              defaultValue={props.filterCriteria[item.id]}
            >
              {(filterControllerProps: FilterControllerDataProps) => (
                <div className={classes.filterContent}>{renderSubFilterComp(filterControllerProps, item)}</div>
              )}
            </LuiFilterController>
          ))}
        </LuiVerticalFilter>
      </CardContent>
    </Card>
  );
};

export default VerticalFilter;
